/**
 * Phase 16.R11 / W3.BUG-4: YouTube settings in Gallery caption view (when YT is targeted).
 * Includes description (separate from caption) and playlist selector.
 */
import React, { memo, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import { platformAccountsAPI } from '../../services/api';
import {
  YOUTUBE_CATEGORY_OPTIONS,
  YOUTUBE_PRIVACY_OPTIONS,
  defaultYoutubeSettings,
  youtubePayloadFromSettings,
} from '../posts/youtubeConstants';

function GalleryYouTubeSettingsField({
  assetId,
  clientId,
  initialValue,
  saving = false,
  onSave,
}) {
  const [value, setValue] = useState(() =>
    defaultYoutubeSettings(initialValue || {}),
  );
  const [playlists, setPlaylists] = useState([]);
  const [playlistsLoading, setPlaylistsLoading] = useState(false);
  const [playlistsError, setPlaylistsError] = useState('');

  useEffect(() => {
    setValue(defaultYoutubeSettings(initialValue || {}));
  }, [assetId, initialValue]);

  useEffect(() => {
    if (!clientId) {
      setPlaylists([]);
      setPlaylistsError('');
      return undefined;
    }
    let cancelled = false;
    setPlaylistsLoading(true);
    setPlaylistsError('');
    platformAccountsAPI
      .getYouTubePlaylists(clientId)
      .then((data) => {
        if (cancelled) return;
        setPlaylists(Array.isArray(data?.playlists) ? data.playlists : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setPlaylists([]);
        setPlaylistsError(
          err?.response?.data?.detail ||
            err?.message ||
            'Failed to load playlists',
        );
      })
      .finally(() => {
        if (!cancelled) setPlaylistsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const update = (patch) => setValue((prev) => ({ ...prev, ...patch }));

  const selectedPlaylistIds = Array.isArray(value.playlistIds)
    ? value.playlistIds
    : [];

  return (
    <Stack spacing={1.25} sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ color: '#8b949e' }}>
        YouTube settings
      </Typography>
      <Typography variant="caption" sx={{ color: '#6e7681' }}>
        Used when this asset is promoted to YouTube. Approving a caption with
        YouTube targeted auto-fills title (first line), description (full caption),
        and tags (hashtags) — still editable below. Defaults: Public, People &amp;
        Blogs. Saved on the asset and passed through to publish.
      </Typography>
      <TextField
        size="small"
        fullWidth
        label="Video title"
        value={value.title}
        onChange={(e) => update({ title: e.target.value })}
        placeholder="YouTube title (defaults from caption title)"
        InputProps={{ sx: { color: '#f0f6fc', fontSize: '0.875rem' } }}
        InputLabelProps={{ sx: { color: '#8b949e' } }}
        sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#0d1117' } }}
      />
      <TextField
        size="small"
        fullWidth
        multiline
        minRows={3}
        label="YouTube description"
        value={value.description}
        onChange={(e) => update({ description: e.target.value })}
        placeholder="YouTube description (separate from the caption)"
        InputProps={{ sx: { color: '#f0f6fc', fontSize: '0.875rem' } }}
        InputLabelProps={{ sx: { color: '#8b949e' } }}
        sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#0d1117' } }}
      />
      <TextField
        size="small"
        fullWidth
        label="Tags"
        value={value.tags}
        onChange={(e) => update({ tags: e.target.value })}
        placeholder="tag1, tag2, tag3"
        InputProps={{ sx: { color: '#f0f6fc', fontSize: '0.875rem' } }}
        InputLabelProps={{ sx: { color: '#8b949e' } }}
        sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#0d1117' } }}
      />
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 140, flex: '1 1 140px' }}>
          <InputLabel sx={{ color: '#8b949e' }}>Visibility</InputLabel>
          <Select
            label="Visibility"
            value={value.privacyStatus}
            onChange={(e) => update({ privacyStatus: e.target.value })}
            sx={{ color: '#f0f6fc', bgcolor: '#0d1117' }}
          >
            {YOUTUBE_PRIVACY_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180, flex: '1 1 180px' }}>
          <InputLabel sx={{ color: '#8b949e' }}>Category</InputLabel>
          <Select
            label="Category"
            value={value.categoryId}
            onChange={(e) => update({ categoryId: e.target.value })}
            sx={{ color: '#f0f6fc', bgcolor: '#0d1117' }}
          >
            {YOUTUBE_CATEGORY_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {playlistsLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" sx={{ color: '#6e7681' }}>
            Loading playlists…
          </Typography>
        </Box>
      )}

      {!playlistsLoading && playlistsError && (
        <Typography variant="caption" sx={{ color: '#f85149' }}>
          {typeof playlistsError === 'string'
            ? playlistsError
            : 'Could not load playlists'}
        </Typography>
      )}

      {!playlistsLoading && !playlistsError && playlists.length === 0 && (
        <Typography variant="caption" sx={{ color: '#6e7681' }}>
          No playlists — create one in YouTube Studio
        </Typography>
      )}

      {!playlistsLoading && playlists.length > 0 && (
        <FormControl size="small" fullWidth>
          <InputLabel sx={{ color: '#8b949e' }}>Playlists</InputLabel>
          <Select
            multiple
            label="Playlists"
            value={selectedPlaylistIds}
            onChange={(e) =>
              update({
                playlistIds: Array.isArray(e.target.value)
                  ? e.target.value
                  : [],
              })
            }
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.length === 0 ? (
                  <Typography sx={{ color: '#6e7681', fontSize: '0.875rem' }}>
                    Select playlists
                  </Typography>
                ) : (
                  selected.map((id) => {
                    const pl = playlists.find((p) => p.id === id);
                    return (
                      <Chip
                        key={id}
                        size="small"
                        label={pl ? pl.title : id}
                        sx={{
                          bgcolor: 'rgba(88, 166, 255, 0.15)',
                          color: '#58a6ff',
                          height: 22,
                          fontSize: '0.75rem',
                        }}
                      />
                    );
                  })
                )}
              </Box>
            )}
            sx={{ color: '#f0f6fc', bgcolor: '#0d1117' }}
          >
            {playlists.map((playlist) => (
              <MenuItem key={playlist.id} value={playlist.id}>
                <ListItemText
                  primary={playlist.title}
                  secondary={`${playlist.itemCount ?? 0} videos`}
                  primaryTypographyProps={{ fontSize: '0.875rem' }}
                  secondaryTypographyProps={{ fontSize: '0.75rem' }}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Button
        size="small"
        variant="outlined"
        startIcon={
          saving ? <CircularProgress size={14} color="inherit" /> : <Save />
        }
        disabled={saving}
        onClick={() => onSave?.(youtubePayloadFromSettings(value))}
      >
        Save YouTube settings
      </Button>
    </Stack>
  );
}

export default memo(GalleryYouTubeSettingsField);
